import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit, Trash2, Package, DollarSign, Tag, Info, 
  TrendingUp, Clock, CheckCircle, XCircle, AlertCircle, 
  Share2, Download, BarChart3, Activity 
} from 'lucide-react';
import { tiresAPI } from '../../services/garageApi';
import ManageStockModal from '../common/ManageStockModal';
import UpdatePriceModal from '../common/UpdatePriceModal';
import ProductHistoryTimeline from '../common/ProductHistoryTimeline';
import ProductAnalytics from '../common/ProductAnalytics';
import { shareProduct } from '../../utils/shareUtils';
import { exportProduct } from '../../utils/exportUtils';
import { formatCurrency } from '../../utils/helpers';

const TireDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tire, setTire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [error, setError] = useState('');

  // Fetch tire data
  const fetchTire = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await tiresAPI.show(id);

      // Handle different response structures
      let tireData = null;
      if (response.data) {
        if (response.data.success !== undefined && response.data.data) {
          tireData = response.data.data;
        } else if (response.data.data) {
          tireData = response.data.data;
        } else {
          tireData = response.data;
        }
      }
      
      if (!tireData) {
        throw new Error('No tire data received');
      }
      
      // Ensure all nested objects exist with defaults
      tireData = {
        ...tireData,
        tire_brand: tireData.tire_brand || tireData.tireBrand || {},
        pattern: tireData.pattern || {},
        tire_type: tireData.tire_type || tireData.tireType || {},
        supplier: tireData.supplier || {},
        made_in: tireData.made_in || tireData.madeIn || {},
        inventories: tireData.inventories || []
      };

      // Calculate total quantity from inventories if not directly available
      if (tireData.inventories && Array.isArray(tireData.inventories) && tireData.inventories.length > 0) {
        const totalQty = tireData.inventories.reduce((sum, inv) => {
          return sum + (parseInt(inv.quantity) || 0);
        }, 0);
        tireData.quantity = tireData.quantity || totalQty;
      }
      
      // Ensure quantity is a number
      tireData.quantity = parseInt(tireData.quantity) || 0;
      
      setTire(tireData);
      
    } catch (error) {
      console.error('Error fetching tire:', error);
      setError('Failed to load tire details');
      setTire(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTire();
  }, [fetchTire]);

  // Handle delete
  const handleDelete = async () => {
    try {
      await tiresAPI.delete(id);
      navigate('/tires', { 
        state: { message: 'Tire product deleted successfully!' } 
      });
    } catch (error) {
      console.error('Error deleting tire:', error);
      alert(
        error.response?.data?.message || 
        'Failed to delete tire. It may be referenced in other records.'
      );
      setShowDeleteModal(false);
    }
  };

  // Helper functions
  const getRatingColor = (rating) => {
    const colors = {
      A: 'green',
      B: 'blue',
      C: 'yellow',
      D: 'orange',
      E: 'red'
    };
    return colors[rating] || 'gray';
  };

  const getStockStatus = (quantity) => {
    const qty = parseInt(quantity || 0);
    if (qty === 0) return { text: 'Out of Stock', color: 'red', icon: XCircle };
    if (qty < 10) return { text: 'Low Stock', color: 'yellow', icon: AlertCircle };
    return { text: 'In Stock', color: 'green', icon: CheckCircle };
  };

  const getNameFromField = (field) => {
    if (!field) return 'N/A';
    if (typeof field === 'string') return field;
    if (typeof field === 'object' && field.name) return field.name;
    return 'N/A';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return dateValue;
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateValue;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Error state
  if (error || !tire) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <XCircle className="w-6 h-6 text-red-600" />
            <h3 className="text-lg font-medium text-red-900">
              {error || 'Tire not found'}
            </h3>
          </div>
          <p className="text-red-700 mb-4">
            Unable to load tire details. The tire may have been deleted or you may not have permission to view it.
          </p>
          <div className="flex gap-3">
            <button
              onClick={fetchTire}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
            <Link
              to="/tires"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Back to Tires
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Destructure tire data for easier access
  const brand = tire.tire_brand || tire.brand || {};
  const pattern = tire.pattern || {};
  const tireType = tire.tire_type || tire.tireType || {};
  const supplier = tire.supplier || {};
  const madeIn = tire.made_in || tire.madeIn || {};
  
  const stockStatus = getStockStatus(tire.quantity);
  const StockIcon = stockStatus.icon;
  
  const costPrice = parseFloat(tire.cost_price || 0);
  const sellingPrice = parseFloat(tire.selling_price || 0);
  const profitMargin = costPrice > 0 ? ((sellingPrice - costPrice) / costPrice * 100).toFixed(2) : 0;

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Info },
    { id: 'specifications', name: 'Specifications', icon: Package },
    { id: 'pricing', name: 'Pricing & Inventory', icon: DollarSign },
    { id: 'history', name: 'History', icon: Clock },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/tires"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tire.size || 'N/A'}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {getNameFromField(brand)} • {getNameFromField(pattern)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                const productLabel = `${tire.size || ''} - ${getNameFromField(brand)}`;
                const result = await shareProduct(productLabel);
                if (result && result.success) {
                  setToastMessage(result.method === 'clipboard' ? 'Link copied to clipboard' : 'Shared successfully');
                  setTimeout(() => setToastMessage(''), 3000);
                }
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            <div className="relative">
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <button
                    onClick={() => { exportProduct(tire, 'tire', 'csv'); setShowExportDropdown(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={() => { exportProduct(tire, 'tire', 'pdf'); setShowExportDropdown(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
                  >
                    Export as PDF
                  </button>
                </div>
              )}
            </div>
            <Link
              to={`/tires/${id}/edit`}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit
            </Link>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start gap-6">
              <div className="w-48 h-48 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                <svg className="w-32 h-32 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  <circle cx="12" cy="12" r="5"/>
                </svg>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`px-3 py-1 bg-${stockStatus.color}-100 text-${stockStatus.color}-700 text-sm font-medium rounded-full flex items-center gap-1`}>
                    <StockIcon className="w-4 h-4" />
                    {stockStatus.text}
                  </span>
                  {tire.active ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                      Inactive
                    </span>
                  )}
                  {tire.run_flat && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                      Run Flat
                    </span>
                  )}
                </div>
                
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{tire.size || 'N/A'}</h2>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Brand:</span>
                    <span className="ml-2 font-medium">{getNameFromField(brand)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Pattern:</span>
                    <span className="ml-2 font-medium">{getNameFromField(pattern)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="ml-2 font-medium">{getNameFromField(tireType)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Construction:</span>
                    <span className="ml-2 font-medium capitalize">{tire.construction_type || 'N/A'}</span>
                  </div>
                </div>

                {/* Performance Ratings */}
                <div className="flex items-center gap-3 mt-4">
                  {tire.fuel_rating && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Fuel:</span>
                      <div className={`w-10 h-10 bg-${getRatingColor(tire.fuel_rating)}-100 text-${getRatingColor(tire.fuel_rating)}-700 rounded-lg flex items-center justify-center font-bold`}>
                        {tire.fuel_rating}
                      </div>
                    </div>
                  )}
                  {tire.water_rating && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Water:</span>
                      <div className={`w-10 h-10 bg-${getRatingColor(tire.water_rating)}-100 text-${getRatingColor(tire.water_rating)}-700 rounded-lg flex items-center justify-center font-bold`}>
                        {tire.water_rating}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            {/* Tab Headers */}
            <div className="border-b border-gray-100">
              <div className="flex items-center gap-1 p-1">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                        activeTab === tab.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-600">DOT Code</p>
                          <p className="font-medium">{tire.dot_formatted || tire.dot || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Date of Production</p>
                          <p className="font-medium">{formatDate(tire.date_of_production)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Made In</p>
                          <p className="font-medium">{getNameFromField(madeIn)}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-600">Supplier</p>
                          <p className="font-medium">{getNameFromField(supplier)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Date Received</p>
                          <p className="font-medium">{formatDate(tire.date_received)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {tire.notes && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-700">{tire.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'specifications' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical Specifications</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Size</span>
                          <span className="font-medium">{tire.size || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Type</span>
                          <span className="font-medium">{getNameFromField(tireType)}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Construction</span>
                          <span className="font-medium capitalize">{tire.construction_type || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Run Flat</span>
                          <span className="font-medium">{tire.run_flat ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Fuel Rating</span>
                          {tire.fuel_rating ? (
                            <span className={`px-2 py-1 bg-${getRatingColor(tire.fuel_rating)}-100 text-${getRatingColor(tire.fuel_rating)}-700 text-sm font-medium rounded`}>
                              {tire.fuel_rating}
                            </span>
                          ) : (
                            <span className="font-medium">N/A</span>
                          )}
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Water Rating</span>
                          {tire.water_rating ? (
                            <span className={`px-2 py-1 bg-${getRatingColor(tire.water_rating)}-100 text-${getRatingColor(tire.water_rating)}-700 text-sm font-medium rounded`}>
                              {tire.water_rating}
                            </span>
                          ) : (
                            <span className="font-medium">N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'pricing' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Cost Price</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(costPrice)}</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-blue-700 mb-1">Selling Price</p>
                        <p className="text-2xl font-bold text-blue-900">{formatCurrency(sellingPrice)}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-700">Profit Margin</p>
                          <p className="text-2xl font-bold text-green-900">
                            {formatCurrency(sellingPrice - costPrice)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-green-700">Margin %</p>
                          <p className="text-2xl font-bold text-green-900">{profitMargin}%</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="bg-orange-50 rounded-lg p-4">
                        <p className="text-sm text-orange-700 mb-1">Tax Rate</p>
                        <p className="text-2xl font-bold text-orange-900">
                          {parseFloat(tire.tax_rate || 0)}%
                        </p>
                        <p className="text-xs text-orange-600 mt-1">
                          {tire.is_tax_inclusive ? 'Tax Inclusive' : 'Tax Exclusive'}
                        </p>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-4">
                        <p className="text-sm text-indigo-700 mb-1">Final Price (incl. tax)</p>
                        <p className="text-2xl font-bold text-indigo-900">
                          {formatCurrency(tire.is_tax_inclusive
                            ? sellingPrice
                            : sellingPrice * (1 + parseFloat(tire.tax_rate || 0) / 100)
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory Status</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Current Stock</p>
                          <p className="text-2xl font-bold text-gray-900">{tire.quantity || 0} units</p>
                        </div>
                        <StockIcon className={`w-12 h-12 text-${stockStatus.color}-500`} />
                      </div>
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Total Inventory Value</span>
                          <span className="text-lg font-bold text-gray-900">
                            {formatCurrency((tire.quantity || 0) * sellingPrice)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Inventory by Location */}
                    {tire.inventories && tire.inventories.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Stock by Location</h4>
                        <div className="space-y-2">
                          {tire.inventories.map((inv, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">
                                {inv.location?.name || `Location ${index + 1}`}
                                {inv.zone && (
                                  <span className="text-gray-400">
                                    {' / '}{inv.zone.name}
                                    {inv.zone.is_system && (
                                      <span className="ml-1 text-yellow-600 text-xs">(Unallocated)</span>
                                    )}
                                  </span>
                                )}
                              </span>
                              <span className="font-medium">{inv.quantity || 0} units</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <ProductHistoryTimeline productType="tire" productId={id} />
              )}

              {activeTab === 'analytics' && (
                <ProductAnalytics productType="tire" productId={id} />
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm text-gray-600">Stock Level</span>
                </div>
                <span className="font-semibold">{tire.quantity || 0}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-600">Value</span>
                </div>
                <span className="font-semibold">
                  {formatCurrency((tire.quantity || 0) * sellingPrice)}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm text-gray-600">Margin</span>
                </div>
                <span className="font-semibold text-green-600">{profitMargin}%</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              <Link
                to={`/tires/${id}/edit`}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Product
              </Link>
              
              <button
                onClick={() => setShowStockModal(true)}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <Activity className="w-4 h-4" />
                Adjust Stock
              </button>
              
              <button
                onClick={() => setShowPriceModal(true)}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <Tag className="w-4 h-4" />
                Update Price
              </button>
            </div>
          </div>

          {/* Product Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Information</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Product ID</p>
                <p className="font-medium">#{tire.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="font-medium">
                  {tire.created_at ? new Date(tire.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Updated</p>
                <p className="font-medium">
                  {tire.updated_at ? new Date(tire.updated_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Tire Product</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this tire product? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Stock Modal */}
      <ManageStockModal
        isOpen={showStockModal}
        onClose={() => setShowStockModal(false)}
        productIds={[id]}
        productLabel={`${tire.size || ''} - ${getNameFromField(brand)}`}
        productType="tire"
        onStockMoved={fetchTire}
      />

      {/* Update Price Modal */}
      <UpdatePriceModal
        isOpen={showPriceModal}
        onClose={() => setShowPriceModal(false)}
        productId={id}
        productType="tire"
        currentCostPrice={costPrice}
        currentSellingPrice={sellingPrice}
        currentTaxRate={parseFloat(tire.tax_rate || 0)}
        currentIsTaxInclusive={tire.is_tax_inclusive || false}
        onPriceUpdated={fetchTire}
      />

      {/* Toast notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white px-5 py-3 rounded-lg shadow-lg z-50 transition-opacity">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default TireDetail;